import { LuPenLine, LuX } from 'react-icons/lu';
import type { LessonSettings, Participant } from '../../shared/protocol';
import { Toggle } from '../components/ui';
import type { LessonConnection } from '../lib/connection';

export interface ParticipantsProps {
  conn: LessonConnection;
  isTeacher: boolean;
  selfId: string;
  me: Participant;
  permitted: boolean;
  participants: Participant[];
  settings: LessonSettings;
  onClose: () => void;
}

export function ParticipantsPanel(p: ParticipantsProps) {
  const teachers = p.participants.filter((x) => x.role === 'teacher');
  const students = p.participants
    .filter((x) => x.role === 'student')
    .sort(
      (a, b) =>
        Number(b.handRaised) - Number(a.handRaised) || Number(b.online) - Number(a.online) || a.name.localeCompare(b.name, 'tr'),
    );
  const online = students.filter((s) => s.online).length;

  return (
    <aside className="panel" aria-label="Katılımcılar">
      <div className="panel-head">
        <h2>Ders</h2>
        <button type="button" className="icon-btn panel-close" aria-label="Paneli kapat" onClick={p.onClose}>
          <LuX />
        </button>
      </div>

      {p.isTeacher ? (
        <section className="panel-section">
          <Toggle
            label="Tüm öğrenciler çizebilsin"
            hint="Kapalıyken yalnızca kalem verdikleriniz çizer"
            checked={p.settings.studentsCanDraw}
            onChange={(v) => p.conn.send({ t: 'settings', studentsCanDraw: v })}
          />
          <Toggle
            label="Sayfa takibini kilitle"
            hint="Öğrenciler sizin sayfanızdan ayrılamaz"
            checked={p.settings.followLocked}
            onChange={(v) => p.conn.send({ t: 'settings', followLocked: v })}
          />
        </section>
      ) : (
        <section className="panel-section">
          <div className={`perm-badge ${p.permitted ? 'is-ok' : ''}`}>{p.permitted ? 'Çizim izniniz var' : 'İzleme modundasınız'}</div>
          {!p.permitted && (
            <button type="button" className={`btn btn-block ${p.me.handRaised ? 'btn-on' : 'btn-primary'}`} onClick={() => p.conn.send({ t: 'hand', raised: !p.me.handRaised })}>
              {p.me.handRaised ? 'Elimi indir' : '✋ El kaldır'}
            </button>
          )}
        </section>
      )}

      <section className="panel-section">
        <h3>Öğretmen</h3>
        <ul className="people">
          {teachers.map((t) => (
            <li key={t.id} className={t.online ? '' : 'is-offline'}>
              <span className={`dot ${t.online ? 'is-on' : ''}`} />
              <span className="person-name">
                {t.name}
                {t.id === p.selfId ? ' (siz)' : ''}
              </span>
            </li>
          ))}
        </ul>
        <h3>
          Öğrenciler <span className="muted">{online} çevrimiçi</span>
        </h3>
        {students.length === 0 && <p className="muted small">Henüz öğrenci katılmadı. Öğrenci bağlantısını paylaşın.</p>}
        <ul className="people">
          {students.map((s) => {
            const pen = p.settings.studentsCanDraw || s.canDraw;
            return (
              <li key={s.id} className={s.online ? '' : 'is-offline'}>
                <span className={`dot ${s.online ? 'is-on' : ''}`} />
                <span className="person-name">
                  {s.name}
                  {s.id === p.selfId ? ' (siz)' : ''}
                </span>
                {s.handRaised && (
                  <span className="hand" title="El kaldırdı">
                    ✋
                  </span>
                )}
                {p.isTeacher ? (
                  <button
                    type="button"
                    className={`pen-toggle ${pen ? 'is-on' : ''}`}
                    disabled={p.settings.studentsCanDraw}
                    onClick={() => p.conn.send({ t: 'perm', participantId: s.id, canDraw: !s.canDraw })}
                    title={p.settings.studentsCanDraw ? 'Herkes çizebilir' : s.canDraw ? 'Kalemi geri al' : 'Kalem ver'}
                    aria-label={s.canDraw ? `${s.name}: kalemi geri al` : `${s.name}: kalem ver`}
                  >
                    <LuPenLine />
                  </button>
                ) : (
                  pen && <LuPenLine className="pen-mini" title="Çizebilir" />
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </aside>
  );
}
